// react
import { SyntheticEvent } from 'react';

// types
import { FilterType } from '../../../@types';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import { Grid, GridProps, SelectChangeEvent } from '@mui/material';

// ui
import { Autocomplete, AutocompleteProps } from '../../fields/Autocomplete/Autocomplete';

// fields
import { Input, InputProps } from '../../fields/Input/Input';
import { Select, SelectProps } from '../../fields/Select/Select';
import { SelectAccount } from '../../fields/SelectAccount/SelectAccount';
import { SelectCategory } from '../../fields/SelectCategory/SelectCategory';
import { SelectComponent } from '../../fields/SelectComponent/SelectComponent';
import { SelectCountry, SelectCountryProps } from '../../fields/SelectCountry/SelectCountry';
import { SelectCurrency, SelectCurrencyProps } from '../../fields/SelectCurrency/SelectCurrency';
import { SelectExpenseType } from '../../fields/SelectExpenseType/SelectExpenseType';
import { SelectTags } from '../../fields/SelectTags/SelectTags';
import { SelectWithdrawalApplication } from '../../fields/SelectWithdrawalApplication/SelectWithdrawalApplication';

// props
export type FilterChangeEvent = SelectChangeEvent<unknown> | SyntheticEvent<Element, Event>;

export type FilterProps = {
	GridProps?: GridProps;
	onChange?: (e: FilterChangeEvent, value: unknown, filter?: FilterProps) => void;
	type: FilterType;
} & Omit<AutocompleteProps, 'onChange'> &
	Omit<SelectProps, 'onChange'> &
	Omit<InputProps, 'onChange'> &
	Omit<SelectCurrencyProps, 'onChange'> &
	Omit<SelectCountryProps, 'onChange'>;

export interface FiltersProps {
	filters: FilterProps[];
	isLoading?: boolean;
}

export const Filters = ({ filters, isLoading = false }: FiltersProps) => {
	// state
	const [searchParams] = useSearchParams();

	// handlers
	const handleChangeFilter = (e: FilterChangeEvent, value: unknown, filter: FilterProps) => {
		// remove search param
		searchParams.delete('q');

		// clear search input
		const elQ = document.querySelector('#form-search-q') as HTMLInputElement;

		if (elQ) {
			elQ.value = '';
		}

		if (filter.onChange) {
			filter.onChange(e, value, filter);
		}
	};

	return (
		<Grid container spacing={2}>
			{filters.map((filter, i) => {
				const { disabled, GridProps, hidden, type, ...propsFilter } = filter;
				const name = String(filter.name);
				const key = `filter-${i}-${name}`;

				if (hidden) {
					return <></>;
				}

				if (['DATE', 'INPUT'].includes(type)) {
					return (
						<Grid item key={key} xs={12} {...GridProps}>
							<Input
								type={type === 'DATE' ? 'date' : 'text'}
								{...propsFilter}
								disabled={disabled || isLoading}
								onChange={(e) => handleChangeFilter(e, e.target.value, filter)}
							/>
						</Grid>
					);
				} else if (type === 'ACCOUNT') {
					return (
						<Grid item key={key} xs={12} {...GridProps}>
							<SelectAccount
								{...propsFilter}
								disabled={disabled || isLoading}
								onChange={(e) => handleChangeFilter(e, e.target.value, filter)}
							/>
						</Grid>
					);
				} else if (type === 'AUTOCOMPLETE') {
					return (
						<Grid item key={key} xs={12} {...GridProps}>
							<Autocomplete
								{...propsFilter}
								disabled={disabled || isLoading}
								onChange={(e, value) => handleChangeFilter(e, value, filter)}
							/>
						</Grid>
					);
				} else if (type === 'CATEGORY') {
					return (
						<Grid item key={key} xs={12} {...GridProps}>
							<SelectCategory
								{...propsFilter}
								disabled={disabled || isLoading}
								onChange={(e) => handleChangeFilter(e, e.target.value, filter)}
							/>
						</Grid>
					);
				} else if (type === 'COMPONENT') {
					return (
						<Grid item key={key} xs={12} {...GridProps}>
							<SelectComponent
								{...propsFilter}
								disabled={disabled || isLoading}
								onChange={(e) => handleChangeFilter(e, e.target.value, filter)}
							/>
						</Grid>
					);
				} else if (type === 'COUNTRY') {
					return (
						<Grid item key={key} xs={12} {...GridProps}>
							<SelectCountry
								{...propsFilter}
								disabled={disabled || isLoading}
								onChange={(e) => handleChangeFilter(e, e.target.value, filter)}
							/>
						</Grid>
					);
				} else if (type === 'CURRENCY') {
					return (
						<Grid item key={key} xs={12} {...GridProps}>
							<SelectCurrency
								{...propsFilter}
								disabled={disabled || isLoading}
								onChange={(e) => handleChangeFilter(e, e.target.value, filter)}
							/>
						</Grid>
					);
				} else if (type === 'EXPENSE_TYPE') {
					return (
						<Grid item key={key} xs={12} {...GridProps}>
							<SelectExpenseType
								{...propsFilter}
								disabled={disabled || isLoading}
								onChange={(e) => handleChangeFilter(e, e.target.value, filter)}
							/>
						</Grid>
					);
				} else if (type === 'TAGS') {
					return (
						<Grid item key={key} xs={12} {...GridProps}>
							<SelectTags
								{...propsFilter}
								disabled={disabled || isLoading}
								onChange={(e) => handleChangeFilter(e, e.target.value, filter)}
							/>
						</Grid>
					);
				} else if (type === 'WITHDRAWAL_APPLICATION') {
					return (
						<Grid item key={key} xs={12} {...GridProps}>
							<SelectWithdrawalApplication
								{...propsFilter}
								disabled={disabled || isLoading}
								onChange={(e) => handleChangeFilter(e, e.target.value, filter)}
							/>
						</Grid>
					);
				} else {
					return (
						<Grid item key={key} xs={12} {...GridProps}>
							<Select
								{...propsFilter}
								disabled={disabled || isLoading}
								onChange={(e) => handleChangeFilter(e, e.target.value, filter)}
							/>
						</Grid>
					);
				}
			})}
		</Grid>
	);
};
